import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import ExerciseGuide from './ExerciseGuide';
import MealPlanner from './MealPlanner';
import UserPreferences from './UserPreferences';
import WeeklyAdviceCard from './WeeklyAdviceCard';
import { datePickerToISO, isoToDatePicker, validateDatePicker } from '../utils/dateUtils';
import LoadingSpinner from './common/LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import { useFeedbackModal } from './common/useFeedbackModal';
import './CompletePregnancyView.css';

function CompletePregnancyView({ userId, initialProfile = null }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(initialProfile);
  const [status, setStatus] = useState(null);
  const [weekAdvice, setWeekAdvice] = useState(null);
  const [mealPlan, setMealPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const { modal, openModal, closeModal } = useFeedbackModal();

  const [formData, setFormData] = useState({
    lmpDate: '',
    dietaryRestrictions: 'NONE',
    hasNausea: false,
    hasAppetiteIssues: false,
    notes: ''
  });

  // Load all data using a provided profile (avoids race condition after save)
  const loadAllDataWithProfile = useCallback(async (profileData) => {
    try {
      setLoading(true);
      
      console.log('✅ Using provided profile:', profileData);
      
      // Ensure we have a valid profile with LMP date
      if (!profileData || !profileData.lmpDate) {
        console.error('Invalid profile provided:', profileData);
        setLoading(false);
        return;
      }
      
      // Convert ISO date to mm/dd/yyyy for display
      setFormData(prev => ({
        ...prev,
        lmpDate: isoToDatePicker(profileData.lmpDate) || '',
        dietaryRestrictions: profileData.dietaryRestrictions || 'NONE',
        hasNausea: profileData.hasNausea || false,
        hasAppetiteIssues: profileData.hasAppetiteIssues || false,
        notes: profileData.notes || ''
      }));
      
      // Load status - backend calculates everything from LMP date
      const lmpDate = profileData.lmpDate;
      
      // Add a small delay to ensure backend has persisted the profile
      await new Promise(resolve => setTimeout(resolve, 300));
      
      try {
        const statusData = await api.getCurrentStatus(userId);
        console.log('✅ Status loaded:', statusData);
        setStatus(statusData);
      } catch (statusError) {
        console.error('⚠️ Error loading status:', statusError);
        // If status fails, calculate from LMP
        try {
          const calculated = await api.calculateFromLMP(lmpDate);
          setStatus({
            currentWeek: calculated.weeks,
            currentDay: calculated.days,
            trimester: calculated.trimester,
            eddDate: calculated.edd,
            daysUntilDue: calculated.daysUntilDue,
            profile: profileData
          });
        } catch (calcError) {
          console.error('Error calculating from LMP:', calcError);
        }
      }
      
      // Load weekly advice
      if (lmpDate) {
        try {
          try {
            const advice = await api.getCurrentWeekAdvice(userId);
            console.log('✅ Weekly advice loaded:', advice);
            setWeekAdvice(advice);
          } catch (adviceError) {
            console.log('⚠️ Current week advice failed, calculating from LMP');
            const calculated = await api.calculateFromLMP(lmpDate);
            const weekAdviceData = await api.getWeekAdvice(calculated.weeks);
            console.log('✅ Week advice loaded from calculation:', weekAdviceData);
            setWeekAdvice(weekAdviceData);
          }
        } catch (adviceError) {
          console.error('⚠️ Error loading weekly advice:', adviceError);
        }
      }
      
      // Load meal plan
      if (lmpDate) {
        try {
          const mealData = await api.getLegacyMealPlan(userId);
          console.log('✅ Meal plan loaded:', mealData);
          setMealPlan(mealData.mealPlan);
        } catch (mealError) {
          console.log('⚠️ Meal plan not available:', mealError);
        }
      }
    } catch (error) {
      console.error('❌ Error loading data with profile:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get profile from user - LMP date comes from user's profile
      let profileData;
      
      // If profile is already set (e.g., after saving), use it
      if (profile && profile.lmpDate) {
        profileData = profile;
        console.log('✅ Using existing profile:', profileData);
      } else {
        // Otherwise, fetch from API
        try {
          profileData = await api.getPregnancyProfile(userId);
          console.log('✅ Profile loaded from API:', profileData);
          
          // Only proceed if we have a valid profile with LMP date
          if (!profileData || !profileData.lmpDate) {
            throw new Error('Profile missing required LMP date');
          }
          
          // Profile exists - hide setup form
          setProfile(profileData);
          setShowSetup(false);
        } catch (error) {
          // If no profile exists (404) or any error occurs (500), show setup form
          // This handles both "not found" and any server errors gracefully
          const status = error.response?.status || error.status;
          if (status === 404 || status === 500 || !profileData) {
            // Silently handle expected 404 - apiClient already handles logging
            // Only log if it's an unexpected error (not 404)
            if (status !== 404) {
              console.log(`⚠️ Profile not available (status: ${status || 'unknown'}). User needs to set up their profile.`);
            }
            setProfile(null);
            setStatus(null);
            setWeekAdvice(null);
            setMealPlan(null);
            setShowSetup(true); // Show setup form
            setLoading(false);
            return; // Exit early - wait for user input
          } else {
            console.error('❌ Unexpected error getting profile:', error);
            setLoading(false);
            return;
          }
        }
      }
      
      // Ensure we have a valid profile with LMP date before proceeding
      if (!profileData || !profileData.lmpDate) {
        console.error('Profile data is missing or invalid:', profileData);
        setLoading(false);
        return;
      }
      
      // Convert ISO date to mm/dd/yyyy for display
      setFormData(prev => ({
        ...prev,
        lmpDate: isoToDatePicker(profileData.lmpDate) || '',
        dietaryRestrictions: profileData.dietaryRestrictions || 'NONE',
        hasNausea: profileData.hasNausea || false,
        hasAppetiteIssues: profileData.hasAppetiteIssues || false,
        notes: profileData.notes || ''
      }));
      
      // Load status - backend calculates everything from LMP date
      const lmpDate = profileData.lmpDate;
      try {
        const statusData = await api.getCurrentStatus(userId);
        console.log('✅ Status loaded:', statusData);
        setStatus(statusData);
      } catch (statusError) {
        console.error('⚠️ Error loading status:', statusError);
        // If status fails, calculate from LMP
        try {
          const calculated = await api.calculateFromLMP(lmpDate);
          setStatus({
            currentWeek: calculated.weeks,
            currentDay: calculated.days,
            trimester: calculated.trimester,
            eddDate: calculated.edd,
            daysUntilDue: calculated.daysUntilDue,
            profile: profileData
          });
        } catch (calcError) {
          console.error('Error calculating from LMP:', calcError);
        }
      }
      
      // Load weekly advice - only if we have LMP date
      if (lmpDate) {
        try {
          // First try to get current week advice
          try {
            const advice = await api.getCurrentWeekAdvice(userId);
            console.log('✅ Weekly advice loaded:', advice);
            setWeekAdvice(advice);
          } catch (adviceError) {
            // If that fails, calculate week from LMP and get week advice directly
            console.log('⚠️ Current week advice failed, calculating from LMP');
            const calculated = await api.calculateFromLMP(lmpDate);
            const weekAdviceData = await api.getWeekAdvice(calculated.weeks);
            console.log('✅ Week advice loaded from calculation:', weekAdviceData);
            setWeekAdvice(weekAdviceData);
          }
        } catch (adviceError) {
          console.error('⚠️ Error loading weekly advice:', adviceError);
          // Continue even if advice fails
        }
      }
      
      // Load meal plan - optional, only if profile exists
      if (lmpDate) {
        try {
          const mealData = await api.getLegacyMealPlan(userId);
          console.log('✅ Meal plan loaded:', mealData);
          setMealPlan(mealData.mealPlan);
        } catch (mealError) {
          console.log('⚠️ Meal plan not available:', mealError);
          // Don't clear mealPlan if it already exists
        }
      }
    } catch (error) {
      console.error('❌ Unexpected error loading data:', error);
      // Don't clear existing data on unexpected errors
    } finally {
      setLoading(false);
    }
  }, [userId, profile]);

  useEffect(() => {
    // If we have an initial profile, use it and load data
    // Otherwise, fetch the profile first
    if (initialProfile && initialProfile.lmpDate) {
      setProfile(initialProfile);
      setShowSetup(false);
      // Load all data using the initial profile
      loadAllDataWithProfile(initialProfile);
    } else {
      loadAllData();
    }
  }, [userId, initialProfile, loadAllDataWithProfile, loadAllData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate date format
      if (!formData.lmpDate || !formData.lmpDate.trim()) {
        openModal({
          title: 'Missing date',
          message: 'Please select your LMP date.',
          type: 'warning'
        });
        return;
      }
      
      const dateValidation = validateDatePicker(formData.lmpDate);
      if (!dateValidation.valid) {
        openModal({
          title: 'Invalid date',
          message: dateValidation.error,
          type: 'error'
        });
        return;
      }
      
      // Convert date picker format (YYYY-MM-DD) to ISO format for backend
      const isoDate = datePickerToISO(formData.lmpDate);
      if (!isoDate) {
        openModal({
          title: 'Invalid date',
          message: 'Please select a valid date.',
          type: 'error'
        });
        return;
      }
      
      // Set loading state
      setLoading(true);
      
      const profileData = {
        userId,
        lmpDate: isoDate,
        eddDate: null, // Will be calculated by backend
        dietaryRestrictions: formData.dietaryRestrictions || 'NONE',
        hasNausea: formData.hasNausea || false,
        hasAppetiteIssues: formData.hasAppetiteIssues || false,
        notes: formData.notes || ''
      };
      
      console.log('Saving profile with data:', profileData);
      const savedProfile = await api.savePregnancyProfile(profileData);
      console.log('Profile saved successfully:', savedProfile);
      
      // Ensure we got a valid response
      if (!savedProfile || !savedProfile.lmpDate) {
        throw new Error('Invalid response from server. Profile was not saved correctly.');
      }
      
      // Hide setup form and set profile immediately
      setShowSetup(false);
      setProfile(savedProfile);
      
      // Dispatch profile-updated event to refresh ProfileContext across the app
      window.dispatchEvent(new CustomEvent('profile-updated', {
        detail: { userId: userId }
      }));
      console.log('📢 Dispatched profile-updated event after saving profile');
      
      // Update form data with saved profile
      if (savedProfile.lmpDate) {
        setFormData(prev => ({
          ...prev,
          lmpDate: isoToDatePicker(savedProfile.lmpDate) || formData.lmpDate,
          dietaryRestrictions: savedProfile.dietaryRestrictions || prev.dietaryRestrictions,
          hasNausea: savedProfile.hasNausea || false,
          hasAppetiteIssues: savedProfile.hasAppetiteIssues || false,
          notes: savedProfile.notes || ''
        }));
      }
      
      // Reload all data with the new profile (this will calculate everything from LMP)
      // Use the saved profile directly instead of fetching again
      try {
        setLoading(true);
        
        // Load status - backend calculates everything from LMP date
        const lmpDate = savedProfile.lmpDate;
        try {
          const statusData = await api.getCurrentStatus(userId);
          console.log('✅ Status loaded:', statusData);
          setStatus(statusData);
        } catch (statusError) {
          console.error('⚠️ Error loading status:', statusError);
          // If status fails, calculate from LMP
          try {
            const calculated = await api.calculateFromLMP(lmpDate);
            setStatus({
              currentWeek: calculated.weeks,
              currentDay: calculated.days,
              trimester: calculated.trimester,
              eddDate: calculated.edd,
              daysUntilDue: calculated.daysUntilDue,
              profile: savedProfile
            });
          } catch (calcError) {
            console.error('Error calculating from LMP:', calcError);
          }
        }
        
        // Load weekly advice
        if (lmpDate) {
          try {
            try {
              const advice = await api.getCurrentWeekAdvice(userId);
              console.log('✅ Weekly advice loaded:', advice);
              setWeekAdvice(advice);
            } catch (adviceError) {
              console.log('⚠️ Current week advice failed, calculating from LMP');
              const calculated = await api.calculateFromLMP(lmpDate);
              const weekAdviceData = await api.getWeekAdvice(calculated.weeks);
              console.log('✅ Week advice loaded from calculation:', weekAdviceData);
              setWeekAdvice(weekAdviceData);
            }
          } catch (adviceError) {
            console.error('⚠️ Error loading weekly advice:', adviceError);
          }
        }
        
        // Load meal plan
        if (lmpDate) {
          try {
            const mealData = await api.getLegacyMealPlan(userId);
            console.log('✅ Meal plan loaded:', mealData);
            setMealPlan(mealData.mealPlan);
          } catch (mealError) {
            console.log('⚠️ Meal plan not available:', mealError);
          }
        }
      } catch (error) {
        console.error('❌ Error loading data after save:', error);
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setLoading(false);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save profile';
      openModal({
        title: 'Save failed',
        message: `Failed to save: ${errorMessage}`,
        type: 'error'
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <div className="complete-pregnancy-view">
        <LoadingSpinner message="Loading your pregnancy information..." />
      </div>
    );
  }


  // Calculate values safely
  const currentWeek = status?.currentWeek || status?.profile?.currentWeek || weekAdvice?.week || weekAdvice?.currentWeek || 0;
  const trimester = status?.trimester || status?.profile?.trimester || weekAdvice?.trimester || 1;
  const eddDate = (status?.eddDate || status?.profile?.eddDate) ? 
    new Date(status.eddDate || status.profile.eddDate).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }) : 'N/A';
  const daysUntilDue = status?.daysUntilDue || status?.profile?.daysUntilDue || 0;

  // Show setup form if no profile exists
  const needsSetup = !profile || showSetup;

  // Setup form component
  const SetupFormSection = () => {
    const isNewSetup = !profile || showSetup;
    return (
    <section className="data-section setup-section">
      <div className="setup-header-inline">
        <h2>
          {isNewSetup ? '🤱 ' : '✏️ '}
          {isNewSetup 
            ? t('pregnancy.setup.title', 'Set Up Your Pregnancy Profile')
            : t('action.edit.profile', 'Edit Pregnancy Profile')
          }
        </h2>
        {profile && showSetup && (
          <button 
            className="btn-close-inline" 
            onClick={() => setShowSetup(false)}
            title="Close"
          >
            ✕
          </button>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="setup-form-inline">
        <div className="form-section-inline">
          <h3>📅 {t('pregnancy.basic.info', 'Basic Information')}</h3>
          
          <div className="form-group required">
            <label htmlFor="lmpDate">
              {t('pregnancy.lmp', 'First Day of Last Menstrual Period (LMP)')} *
            </label>
            <input
              type="date"
              id="lmpDate"
              name="lmpDate"
              value={formData.lmpDate}
              onChange={handleChange}
              required
              max={new Date().toISOString().split('T')[0]} // Prevent future dates
            />
            <small className="form-help">
              {t('pregnancy.lmp.help', 'Select the first day of your last menstrual period. Your due date will be calculated automatically (40 weeks from LMP)')}
            </small>
          </div>
          
        </div>

        <div className="form-section-inline">
          <h3>💡 {t('pregnancy.additional.info', 'Additional Information (Optional)')}</h3>
          
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="hasNausea"
                checked={formData.hasNausea}
                onChange={handleChange}
              />
              <span>🤢 {t('pregnancy.nausea', 'Experiencing nausea')}</span>
            </label>
            
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="hasAppetiteIssues"
                checked={formData.hasAppetiteIssues}
                onChange={handleChange}
              />
              <span>🍽️ {t('pregnancy.appetite', 'Appetite issues')}</span>
            </label>
          </div>
          
          <div className="form-group">
            <label htmlFor="notes">
              📝 {t('pregnancy.notes', 'Additional Notes')}
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder={t('pregnancy.notes.placeholder', 'Any additional information you\'d like to share...')}
            />
          </div>
        </div>

        <button type="submit" className="btn-submit-inline">
          💾 {t('pregnancy.save.start', 'Save & Start Tracking')}
        </button>
      </form>
    </section>
    );
  };

  return (
    <div className="complete-pregnancy-view">
      {/* Setup Form Section - Show if no profile or if editing */}
      {needsSetup && <SetupFormSection />}

      {/* Header Stats - Show if we have profile data or calculated data */}
      {(profile || status) && (
        <div className="view-header">
          <div className="header-stats">
            <div className="stat-box">
              <div className="stat-number">{currentWeek || '?'}</div>
              <div className="stat-label">{t('pregnancy.week', 'Week')}</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">
                {trimester === 1 && '1️⃣'}
                {trimester === 2 && '2️⃣'}
                {trimester === 3 && '3️⃣'}
                {!trimester && '?'}
              </div>
              <div className="stat-label">{t('pregnancy.trimester', 'Trimester')}</div>
            </div>
            <div className="stat-box">
              <div className="stat-number-small">{eddDate}</div>
              <div className="stat-label">{t('pregnancy.due.date', 'Due Date')}</div>
              {daysUntilDue > 0 && (
                <div className="stat-subtitle">{daysUntilDue} {t('pregnancy.days.until', 'days until')}</div>
              )}
            </div>
          </div>
          {!showSetup && (
            <button className="btn-edit" onClick={() => setShowSetup(true)}>
              ✏️ {t('action.edit', 'Edit Profile')}
            </button>
          )}
        </div>
      )}

      {/* All Data Sections - Only show if we have a profile */}
      {profile && (
      <div className="data-sections">
        {/* Tabs Navigation */}
        <div className="tabs-container">
          <div className="tabs-nav">
            <button 
              className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              📊 Overview
            </button>
            <button 
              className={`tab-button ${activeTab === 'nutrition' ? 'active' : ''}`}
              onClick={() => setActiveTab('nutrition')}
            >
              🥗 Nutrition
            </button>
            <button 
              className={`tab-button ${activeTab === 'exercise' ? 'active' : ''}`}
              onClick={() => setActiveTab('exercise')}
            >
              🏃 Exercise
            </button>
            <button 
              className={`tab-button ${activeTab === 'appointments' ? 'active' : ''}`}
              onClick={() => setActiveTab('appointments')}
            >
              📅 Appointments
            </button>
            <button 
              className={`tab-button ${activeTab === 'warnings' ? 'active' : ''}`}
              onClick={() => setActiveTab('warnings')}
            >
              ⚠️ Warnings
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tabs-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="tab-panel">
        {/* Show loading message if data is still loading */}
        {loading && (
          <section className="data-section">
            <div className="loading-message">
              <LoadingSpinner message="Loading your pregnancy information..." />
            </div>
          </section>
        )}

        {/* Summary for This Week */}
        {!loading && weekAdvice && (
          <section className="data-section summary-section">
            <h2>📋 {t('pregnancy.week.summary', 'Summary for This Week')}</h2>
            <div className="nutrient-foods-grid">
              {weekAdvice.baby && (
                <div className="nutrient-category">
                  <h3>👶 {t('pregnancy.baby.development', 'Baby Development')}</h3>
                  <p style={{marginBottom: '10px'}}>{weekAdvice.baby.development}</p>
                  {weekAdvice.baby.size && (
                    <p style={{margin: '5px 0', fontSize: '0.9rem', color: '#666'}}>📏 {weekAdvice.baby.size}</p>
                  )}
                  {weekAdvice.baby.weight && (
                    <p style={{margin: '5px 0', fontSize: '0.9rem', color: '#666'}}>⚖️ {weekAdvice.baby.weight}</p>
                  )}
                </div>
              )}
              {weekAdvice.mother && (
                <div className="nutrient-category">
                  <h3>👩 {t('pregnancy.mother.changes', "Mother's Changes")}</h3>
                  <p style={{marginBottom: '10px'}}>{weekAdvice.mother.physicalChanges}</p>
                  {weekAdvice.mother.energyLevel && (
                    <p style={{margin: '5px 0', fontSize: '0.9rem', color: '#666'}}>⚡ {weekAdvice.mother.energyLevel}</p>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* What to Expect */}
        {!loading && weekAdvice && (
          <section className="data-section expect-section">
            <h2>🔮 {t('pregnancy.what.to.expect', 'What to Expect This Week')}</h2>
            <div className="nutrient-foods-grid">
              {weekAdvice.mother?.symptoms && weekAdvice.mother.symptoms.length > 0 && (
                <div className="nutrient-category">
                  <h3>💡 {t('pregnancy.weekly.symptoms', 'Weekly Symptoms')}</h3>
                  <ul>
                    {weekAdvice.mother.symptoms.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {weekAdvice.baby?.milestones && weekAdvice.baby.milestones.length > 0 && (
                <div className="nutrient-category">
                  <h3>✨ {t('pregnancy.milestones', 'Baby Milestones')}</h3>
                  <ul>
                    {weekAdvice.baby.milestones.map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Weekly Fetal Development */}
        {!loading && weekAdvice?.baby && (
          <section className="data-section baby-section">
            <h2>👶 {t('pregnancy.weekly.fetal.development', 'Weekly Fetal Development')}</h2>
            <div className="nutrient-foods-grid">
              <div className="nutrient-category">
                <h3>📝 {t('pregnancy.development', 'Development')}</h3>
                <p style={{marginBottom: '10px'}}>{weekAdvice.baby.development}</p>
                {weekAdvice.baby.size && (
                  <p style={{margin: '5px 0', fontSize: '0.9rem', color: '#666'}}>📏 {weekAdvice.baby.size}</p>
                )}
                {weekAdvice.baby.weight && (
                  <p style={{margin: '5px 0', fontSize: '0.9rem', color: '#666'}}>⚖️ {weekAdvice.baby.weight}</p>
                )}
              </div>
              {weekAdvice.baby.milestones && weekAdvice.baby.milestones.length > 0 && (
                <div className="nutrient-category">
                  <h3>✨ {t('pregnancy.milestones', 'Milestones')}</h3>
                  <ul>
                    {weekAdvice.baby.milestones.map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Weekly Symptoms */}
        {!loading && weekAdvice?.mother?.symptoms && weekAdvice.mother.symptoms.length > 0 && (
          <section className="data-section symptoms-section">
            <h2>💡 {t('pregnancy.weekly.symptoms', 'Weekly Symptoms')}</h2>
            <div className="nutrient-foods-grid">
              <div className="nutrient-category">
                <h3>💡 {t('pregnancy.symptoms', 'Symptoms')}</h3>
                <ul>
                  {weekAdvice.mother.symptoms.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* Mother's Changes */}
        {weekAdvice?.mother && (
          <section className="data-section mother-section">
            <h2>👩 {t('pregnancy.mother.changes', "Mother's Changes")}</h2>
            <div className="nutrient-foods-grid">
              <div className="nutrient-category">
                <h3>📝 {t('pregnancy.physical.changes', 'Physical Changes')}</h3>
                <p style={{marginBottom: '10px'}}>{weekAdvice.mother.physicalChanges}</p>
                {weekAdvice.mother.energyLevel && (
                  <p style={{margin: '5px 0', fontSize: '0.9rem', color: '#666'}}>⚡ {weekAdvice.mother.energyLevel}</p>
                )}
              </div>
              {weekAdvice.mother.symptoms && weekAdvice.mother.symptoms.length > 0 && (
                <div className="nutrient-category">
                  <h3>💡 {t('pregnancy.symptoms', 'Symptoms')}</h3>
                  <ul>
                    {weekAdvice.mother.symptoms.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Recommendations */}
        {!loading && weekAdvice?.advice && (
          <section className="data-section recommendations-section">
            <h2>💡 {t('pregnancy.recommendations', 'Recommendations')}</h2>
            <div className="nutrient-foods-grid">
              {weekAdvice.advice.nutrition && (
                <div className="nutrient-category">
                  <h3>🥗 {t('pregnancy.nutrition', 'Nutrition')}</h3>
                  {weekAdvice.advice.nutrition.focus && weekAdvice.advice.nutrition.focus.length > 0 && (
                    <div style={{marginBottom: '15px'}}>
                      <strong style={{display: 'block', marginBottom: '8px', fontSize: '0.95rem'}}>✅ {t('pregnancy.focus.on', 'Focus On')}</strong>
                      <ul>
                        {weekAdvice.advice.nutrition.focus.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {weekAdvice.advice.nutrition.avoid && weekAdvice.advice.nutrition.avoid.length > 0 && (
                    <div>
                      <strong style={{display: 'block', marginBottom: '8px', fontSize: '0.95rem'}}>❌ {t('pregnancy.avoid', 'Avoid')}</strong>
                      <ul>
                        {weekAdvice.advice.nutrition.avoid.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {weekAdvice.advice.activity && (
                <div className="nutrient-category">
                  <h3>🏃 {t('pregnancy.activity', 'Activity')}</h3>
                  {weekAdvice.advice.activity.recommended && weekAdvice.advice.activity.recommended.length > 0 && (
                    <div style={{marginBottom: '15px'}}>
                      <strong style={{display: 'block', marginBottom: '8px', fontSize: '0.95rem'}}>✅ {t('pregnancy.recommended', 'Recommended')}</strong>
                      <ul>
                        {weekAdvice.advice.activity.recommended.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {weekAdvice.advice.activity.avoid && weekAdvice.advice.activity.avoid.length > 0 && (
                    <div>
                      <strong style={{display: 'block', marginBottom: '8px', fontSize: '0.95rem'}}>❌ {t('pregnancy.avoid', 'Avoid')}</strong>
                      <ul>
                        {weekAdvice.advice.activity.avoid.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Trimester-Specific Nutrition */}
        {!loading && mealPlan?.nutritionGuidelines && (
          <section className="data-section nutrition-guidelines-section">
            <h2>🥗 {t('pregnancy.trimester.nutrition', 'What Nutrients Matter Each Trimester')}</h2>
            <div className="nutrient-foods-grid">
              {mealPlan.nutritionGuidelines.calories && (
                <div className="nutrient-category">
                  <h3>🔥 {t('pregnancy.calories', 'Calories')}</h3>
                  <p>{mealPlan.nutritionGuidelines.calories} kcal</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.protein && (
                <div className="nutrient-category">
                  <h3>🥩 {t('pregnancy.protein', 'Protein')}</h3>
                  <p>{mealPlan.nutritionGuidelines.protein}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.iron && (
                <div className="nutrient-category">
                  <h3>🩸 {t('pregnancy.iron', 'Iron')}</h3>
                  <p>{mealPlan.nutritionGuidelines.iron}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.calcium && (
                <div className="nutrient-category">
                  <h3>🥛 {t('pregnancy.calcium', 'Calcium')}</h3>
                  <p>{mealPlan.nutritionGuidelines.calcium}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.folate && (
                <div className="nutrient-category">
                  <h3>🌿 {t('pregnancy.folate', 'Folate')}</h3>
                  <p>{mealPlan.nutritionGuidelines.folate}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.omega3 && (
                <div className="nutrient-category">
                  <h3>🐟 {t('pregnancy.omega3', 'Omega-3')}</h3>
                  <p>{mealPlan.nutritionGuidelines.omega3}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.fiber && (
                <div className="nutrient-category">
                  <h3>🌾 {t('pregnancy.fiber', 'Fiber')}</h3>
                  <p>{mealPlan.nutritionGuidelines.fiber}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.mealFrequency && (
                <div className="nutrient-category">
                  <h3>🍽️ {t('pregnancy.meal.frequency', 'Meal Frequency')}</h3>
                  <p>{mealPlan.nutritionGuidelines.mealFrequency}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Foods High in Key Nutrients */}
        {!loading && (
        <section className="data-section key-foods-section">
          <h2>🥘 {t('pregnancy.key.nutrient.foods', 'Foods High in Key Nutrients')}</h2>
          <div className="nutrient-foods-grid">
            <div className="nutrient-category">
              <h3>🩸 {t('pregnancy.iron.rich', 'Iron-Rich Foods')}</h3>
              <ul>
                <li>🥩 Lean red meat</li>
                <li>🌿 Dark leafy greens (spinach, kale)</li>
                <li>🫘 Lentils and beans</li>
                <li>🥜 Nuts and seeds</li>
                <li>🍞 Fortified cereals</li>
                <li>🐟 Fish (cooked thoroughly)</li>
              </ul>
            </div>
            <div className="nutrient-category">
              <h3>🌿 {t('pregnancy.folate.rich', 'Folate-Rich Foods')}</h3>
              <ul>
                <li>🥬 Dark leafy greens</li>
                <li>🫘 Legumes (lentils, chickpeas)</li>
                <li>🍊 Citrus fruits</li>
                <li>🥑 Avocado</li>
                <li>🥜 Nuts and seeds</li>
                <li>🌾 Fortified grains</li>
              </ul>
            </div>
            <div className="nutrient-category">
              <h3>🥩 {t('pregnancy.protein.rich', 'Protein-Rich Foods')}</h3>
              <ul>
                <li>🥩 Lean meats</li>
                <li>🐟 Fish (cooked thoroughly)</li>
                <li>🥚 Eggs (fully cooked)</li>
                <li>🫘 Beans and lentils</li>
                <li>🥛 Dairy products</li>
                <li>🥜 Nuts and seeds</li>
              </ul>
            </div>
          </div>
        </section>
        )}

        {/* Foods to Avoid */}
        {!loading && mealPlan?.avoidFoods && mealPlan.avoidFoods.length > 0 && (
          <section className="data-section avoid-foods-section">
            <h2>❌ {t('pregnancy.foods.to.avoid', 'Foods to Avoid')}</h2>
            <div className="nutrient-foods-grid">
              <div className="nutrient-category" style={{position: 'relative', overflow: 'hidden', paddingLeft: 'calc(20px + 16px)'}}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '4px',
                  height: '100%',
                  background: 'linear-gradient(180deg, #c62828 0%, #d32f2f 50%, #e53935 100%)',
                  borderRadius: '8px 0 0 8px',
                  opacity: 0.8
                }}></div>
                <h3>🚫 {t('pregnancy.avoid.list', 'Foods to Avoid During Pregnancy')}</h3>
                <ul>
                  {mealPlan.avoidFoods.map((food, i) => (
                    <li key={i}>{food}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* Hydration Needs */}
        {!loading && weekAdvice?.advice?.hydration && (
          <section className="data-section hydration-section">
            <h2>💧 {t('pregnancy.hydration.needs', 'Hydration Needs')}</h2>
            <div className="nutrient-foods-grid">
              <div className="nutrient-category">
                <h3>💧 {t('pregnancy.hydration.info', 'Hydration Information')}</h3>
                <p style={{marginBottom: '15px'}}>{weekAdvice.advice.hydration}</p>
                <strong style={{display: 'block', marginBottom: '8px', fontSize: '0.95rem'}}>💡 {t('pregnancy.hydration.tips', 'Hydration Tips')}</strong>
                <ul>
                  <li>💧 Aim for 8-10 glasses of water per day</li>
                  <li>🥤 Include hydrating foods (fruits, vegetables)</li>
                  <li>🚫 Limit caffeine intake</li>
                  <li>⚡ Increase intake during exercise or hot weather</li>
                  <li>🚨 Watch for signs of dehydration (dark urine, dizziness)</li>
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* Meal Recommendations */}
        {!loading && mealPlan && (
          <section className="data-section meals-section">
            <h2>🍽️ {t('pregnancy.meal.recommendations', 'Meal Recommendations')}</h2>
            <MealPlanner userId={userId} />
          </section>
        )}

        {/* Exercise */}
        {!loading && weekAdvice?.exercise && (
          <section className="data-section exercise-section">
            <h2>🏃 {t('pregnancy.exercise', 'Exercise')}</h2>
            <ExerciseGuide exerciseInfo={weekAdvice.exercise} />
          </section>
        )}

        {/* Weekly Health Recommendations */}
        {!loading && weekAdvice?.advice?.health && (
          <section className="data-section health-section">
            <h2>🏥 {t('pregnancy.weekly.health.recommendations', 'Weekly Health Recommendations')}</h2>
            <div className="nutrient-foods-grid">
              {weekAdvice.advice.health.appointments && weekAdvice.advice.health.appointments.length > 0 && (
                <div className="nutrient-category">
                  <h3>📅 {t('pregnancy.appointments', 'Appointments')}</h3>
                  <ul>
                    {weekAdvice.advice.health.appointments.map((a, i) => (
                      <li key={i}>{a}</li>
                    ))}
                  </ul>
                </div>
              )}
              {weekAdvice.advice.health.tests && weekAdvice.advice.health.tests.length > 0 && (
                <div className="nutrient-category">
                  <h3>🧪 {t('pregnancy.tests', 'Tests')}</h3>
                  <ul>
                    {weekAdvice.advice.health.tests.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}
              {weekAdvice.advice.health.warnings && weekAdvice.advice.health.warnings.length > 0 && (
                <div className="nutrient-category" style={{position: 'relative', overflow: 'hidden', paddingLeft: 'calc(20px + 16px)'}}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '4px',
                  height: '100%',
                  background: 'linear-gradient(180deg, #c62828 0%, #d32f2f 50%, #e53935 100%)',
                  borderRadius: '8px 0 0 8px',
                  opacity: 0.8
                }}></div>
                  <h3>⚠️ {t('pregnancy.warnings', 'Warnings')}</h3>
                  <ul>
                    {weekAdvice.advice.health.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ACOG Appointments Needed */}
        {!loading && weekAdvice?.acogAppointments && (
          <section className="data-section acog-appointments-section">
            <h2>📅 {t('pregnancy.acog.appointments', 'Appointments Needed (ACOG Guidelines)')}</h2>
            <div className="nutrient-foods-grid">
              {weekAdvice.acogAppointments.currentWeek && (
                <div className="nutrient-category" style={{position: 'relative', overflow: 'hidden', paddingLeft: 'calc(20px + 16px)'}}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: 'linear-gradient(180deg, #1976d2 0%, #2196f3 50%, #42a5f5 100%)',
                    borderRadius: '8px 0 0 8px',
                    opacity: 0.8
                  }}></div>
                  <h3>📋 {weekAdvice.acogAppointments.currentWeek.name || 'Current Week Appointment'}</h3>
                  <p style={{marginBottom: '10px', fontWeight: 'bold'}}>
                    Week {weekAdvice.acogAppointments.currentWeek.week}
                  </p>
                  {weekAdvice.acogAppointments.currentWeek.description && (
                    <p style={{marginBottom: '10px'}}>{weekAdvice.acogAppointments.currentWeek.description}</p>
                  )}
                  {weekAdvice.acogAppointments.currentWeek.includes && weekAdvice.acogAppointments.currentWeek.includes.length > 0 && (
                    <div>
                      <strong style={{display: 'block', marginBottom: '8px', fontSize: '0.95rem'}}>This visit includes:</strong>
                      <ul>
                        {weekAdvice.acogAppointments.currentWeek.includes.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {weekAdvice.acogAppointments.upcoming && weekAdvice.acogAppointments.upcoming.length > 0 && (
                <div className="nutrient-category">
                  <h3>🔜 {t('pregnancy.upcoming.appointments', 'Upcoming Appointments (Next 4 Weeks)')}</h3>
                  {weekAdvice.acogAppointments.upcoming.map((appt, i) => (
                    <div key={i} style={{marginBottom: '15px', paddingBottom: '15px', borderBottom: i < weekAdvice.acogAppointments.upcoming.length - 1 ? '1px solid #e0e0e0' : 'none'}}>
                      <strong style={{display: 'block', marginBottom: '5px'}}>Week {appt.week}: {appt.name}</strong>
                      {appt.description && (
                        <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '5px'}}>{appt.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {weekAdvice.acogAppointments.timeline && weekAdvice.acogAppointments.timeline.length > 0 && (
                <div className="nutrient-category">
                  <h3>📊 {t('pregnancy.appointment.timeline', 'Appointment Schedule')}</h3>
                  {weekAdvice.acogAppointments.timeline.map((period, i) => (
                    <div key={i} style={{marginBottom: '15px'}}>
                      <strong style={{display: 'block', marginBottom: '5px'}}>Weeks {period.weeks}</strong>
                      <p style={{fontSize: '0.9rem', color: '#666'}}>
                        {period.frequency}: {period.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* ACOG Warning Signs to Watch */}
        {!loading && weekAdvice?.acogWarningSigns && (
          <section className="data-section acog-warnings-section">
            <h2>🚨 {t('pregnancy.acog.warning.signs', 'Warning Signs to Watch (ACOG Red Flags)')}</h2>
            <div className="nutrient-foods-grid">
              {weekAdvice.acogWarningSigns.redFlags && weekAdvice.acogWarningSigns.redFlags.length > 0 && (
                <div className="nutrient-category nutrient-category-warning" style={{backgroundColor: '#fff5f5'}}>
                  <h3>⚠️ {t('pregnancy.red.flags', 'Red Flags - Call Doctor Immediately')}</h3>
                  {weekAdvice.acogWarningSigns.redFlags.map((category, i) => (
                    <div key={i} style={{marginBottom: '20px'}}>
                      <strong style={{display: 'block', marginBottom: '8px', color: '#c62828'}}>
                        {category.category}:
                      </strong>
                      {category.signs && category.signs.length > 0 && (
                        <ul style={{marginBottom: '8px'}}>
                          {category.signs.map((sign, j) => (
                            <li key={j} style={{marginBottom: '5px'}}>{sign}</li>
                          ))}
                        </ul>
                      )}
                      {category.action && (
                        <p style={{fontSize: '0.9rem', color: '#c62828', fontWeight: 'bold', marginTop: '8px'}}>
                          ⚡ {category.action}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {weekAdvice.acogWarningSigns.whenToCall && (
                <div className="nutrient-category">
                  <h3>📞 {t('pregnancy.when.to.call', 'When to Call Your Doctor')}</h3>
                  {weekAdvice.acogWarningSigns.whenToCall.immediately && weekAdvice.acogWarningSigns.whenToCall.immediately.length > 0 && (
                    <div style={{marginBottom: '15px'}}>
                      <strong style={{display: 'block', marginBottom: '8px', color: '#c62828'}}>
                        🚨 Call Immediately:
                      </strong>
                      <ul>
                        {weekAdvice.acogWarningSigns.whenToCall.immediately.map((item, i) => (
                          <li key={i} style={{color: '#c62828'}}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {weekAdvice.acogWarningSigns.whenToCall.within24Hours && weekAdvice.acogWarningSigns.whenToCall.within24Hours.length > 0 && (
                    <div style={{marginBottom: '15px'}}>
                      <strong style={{display: 'block', marginBottom: '8px', color: '#f57c00'}}>
                        ⏰ Call Within 24 Hours:
                      </strong>
                      <ul>
                        {weekAdvice.acogWarningSigns.whenToCall.within24Hours.map((item, i) => (
                          <li key={i} style={{color: '#f57c00'}}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {weekAdvice.acogWarningSigns.whenToCall.routine && weekAdvice.acogWarningSigns.whenToCall.routine.length > 0 && (
                    <div>
                      <strong style={{display: 'block', marginBottom: '8px', color: '#1976d2'}}>
                        📋 Routine Questions:
                      </strong>
                      <ul>
                        {weekAdvice.acogWarningSigns.whenToCall.routine.map((item, i) => (
                          <li key={i} style={{color: '#1976d2'}}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Sleep, Mental Health, Hydration */}
        {!loading && weekAdvice?.advice && (
          <section className="data-section wellness-section">
            <h2>🧘 {t('pregnancy.wellness', 'Wellness')}</h2>
            <div className="nutrient-foods-grid">
              {weekAdvice.advice.sleep && (
                <div className="nutrient-category">
                  <h3>😴 {t('pregnancy.sleep', 'Sleep Tips')}</h3>
                  <p>{weekAdvice.advice.sleep}</p>
                </div>
              )}
              {weekAdvice.advice.mentalHealth && (
                <div className="nutrient-category">
                  <h3>🧘 {t('pregnancy.mental.health', 'Mental Health')}</h3>
                  <p>{weekAdvice.advice.mentalHealth}</p>
                </div>
              )}
              {weekAdvice.advice.hydration && (
                <div className="nutrient-category">
                  <h3>💧 {t('pregnancy.hydration', 'Hydration')}</h3>
                  <p>{weekAdvice.advice.hydration}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Weekly Checklist */}
        {!loading && weekAdvice?.checklist && weekAdvice.checklist.length > 0 && (
          <section className="data-section checklist-section">
            <h2>✅ {t('pregnancy.weekly.checklist', 'Weekly Checklist')}</h2>
            <div className="nutrient-foods-grid">
              <div className="nutrient-category">
                <h3>📋 {t('pregnancy.checklist.items', 'This Week\'s Checklist')}</h3>
                <div style={{marginTop: '10px'}}>
                  {weekAdvice.checklist.map((item, i) => (
                    <div key={i} style={{display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: i < weekAdvice.checklist.length - 1 ? '1px solid #e0e0e0' : 'none'}}>
                      <input type="checkbox" id={`check-${i}`} style={{width: '18px', height: '18px', cursor: 'pointer'}} />
                      <label htmlFor={`check-${i}`} style={{cursor: 'pointer', color: '#555', flex: 1}}>{item}</label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* User Preferences */}
        {!loading && (
          <section className="data-section preferences-section">
            <UserPreferences userId={userId} onSave={loadAllData} />
          </section>
        )}

        {/* Full Weekly Advice Card */}
        {!loading && weekAdvice && (
          <section className="data-section full-advice-section">
            <h2>📋 {t('pregnancy.full.advice', 'Complete Weekly Advice')}</h2>
            <WeeklyAdviceCard weekInfo={weekAdvice} />
          </section>
        )}
            </div>
          )}

          {/* Nutrition Tab */}
          {activeTab === 'nutrition' && (
            <div className="tab-panel">
        {/* Trimester-Specific Nutrition */}
        {!loading && mealPlan?.nutritionGuidelines && (
          <section className="data-section nutrition-guidelines-section">
            <h2>🥗 {t('pregnancy.trimester.nutrition', 'What Nutrients Matter Each Trimester')}</h2>
            <div className="nutrient-foods-grid">
              {mealPlan.nutritionGuidelines.calories && (
                <div className="nutrient-category">
                  <h3>🔥 {t('pregnancy.calories', 'Calories')}</h3>
                  <p>{mealPlan.nutritionGuidelines.calories} kcal</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.protein && (
                <div className="nutrient-category">
                  <h3>🥩 {t('pregnancy.protein', 'Protein')}</h3>
                  <p>{mealPlan.nutritionGuidelines.protein}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.iron && (
                <div className="nutrient-category">
                  <h3>🩸 {t('pregnancy.iron', 'Iron')}</h3>
                  <p>{mealPlan.nutritionGuidelines.iron}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.calcium && (
                <div className="nutrient-category">
                  <h3>🥛 {t('pregnancy.calcium', 'Calcium')}</h3>
                  <p>{mealPlan.nutritionGuidelines.calcium}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.folate && (
                <div className="nutrient-category">
                  <h3>🌿 {t('pregnancy.folate', 'Folate')}</h3>
                  <p>{mealPlan.nutritionGuidelines.folate}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.omega3 && (
                <div className="nutrient-category">
                  <h3>🐟 {t('pregnancy.omega3', 'Omega-3')}</h3>
                  <p>{mealPlan.nutritionGuidelines.omega3}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.fiber && (
                <div className="nutrient-category">
                  <h3>🌾 {t('pregnancy.fiber', 'Fiber')}</h3>
                  <p>{mealPlan.nutritionGuidelines.fiber}</p>
                </div>
              )}
              {mealPlan.nutritionGuidelines.mealFrequency && (
                <div className="nutrient-category">
                  <h3>🍽️ {t('pregnancy.meal.frequency', 'Meal Frequency')}</h3>
                  <p>{mealPlan.nutritionGuidelines.mealFrequency}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Foods High in Key Nutrients */}
        {!loading && (
        <section className="data-section key-foods-section">
          <h2>🥘 {t('pregnancy.key.nutrient.foods', 'Foods High in Key Nutrients')}</h2>
          <div className="nutrient-foods-grid">
            <div className="nutrient-category">
              <h3>🩸 {t('pregnancy.iron.rich', 'Iron-Rich Foods')}</h3>
              <ul>
                <li>🥩 Lean red meat</li>
                <li>🌿 Dark leafy greens (spinach, kale)</li>
                <li>🫘 Lentils and beans</li>
                <li>🥜 Nuts and seeds</li>
                <li>🍞 Fortified cereals</li>
                <li>🐟 Fish (cooked thoroughly)</li>
              </ul>
            </div>
            <div className="nutrient-category">
              <h3>🌿 {t('pregnancy.folate.rich', 'Folate-Rich Foods')}</h3>
              <ul>
                <li>🥬 Dark leafy greens</li>
                <li>🫘 Legumes (lentils, chickpeas)</li>
                <li>🍊 Citrus fruits</li>
                <li>🥑 Avocado</li>
                <li>🥜 Nuts and seeds</li>
                <li>🌾 Fortified grains</li>
              </ul>
            </div>
            <div className="nutrient-category">
              <h3>🥩 {t('pregnancy.protein.rich', 'Protein-Rich Foods')}</h3>
              <ul>
                <li>🥩 Lean meats</li>
                <li>🐟 Fish (cooked thoroughly)</li>
                <li>🥚 Eggs (fully cooked)</li>
                <li>🫘 Beans and lentils</li>
                <li>🥛 Dairy products</li>
                <li>🥜 Nuts and seeds</li>
              </ul>
            </div>
          </div>
        </section>
        )}

        {/* Foods to Avoid */}
        {!loading && mealPlan?.avoidFoods && mealPlan.avoidFoods.length > 0 && (
          <section className="data-section avoid-foods-section">
            <h2>❌ {t('pregnancy.foods.to.avoid', 'Foods to Avoid')}</h2>
            <div className="nutrient-foods-grid">
              <div className="nutrient-category" style={{position: 'relative', overflow: 'hidden', paddingLeft: 'calc(20px + 16px)'}}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '4px',
                  height: '100%',
                  background: 'linear-gradient(180deg, #c62828 0%, #d32f2f 50%, #e53935 100%)',
                  borderRadius: '8px 0 0 8px',
                  opacity: 0.8
                }}></div>
                <h3>🚫 {t('pregnancy.avoid.list', 'Foods to Avoid During Pregnancy')}</h3>
                <ul>
                  {mealPlan.avoidFoods.map((food, i) => (
                    <li key={i}>{food}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* Hydration Needs */}
        {!loading && weekAdvice?.advice?.hydration && (
          <section className="data-section hydration-section">
            <h2>💧 {t('pregnancy.hydration.needs', 'Hydration Needs')}</h2>
            <div className="nutrient-foods-grid">
              <div className="nutrient-category">
                <h3>💧 {t('pregnancy.hydration.info', 'Hydration Information')}</h3>
                <p style={{marginBottom: '15px'}}>{weekAdvice.advice.hydration}</p>
                <strong style={{display: 'block', marginBottom: '8px', fontSize: '0.95rem'}}>💡 {t('pregnancy.hydration.tips', 'Hydration Tips')}</strong>
                <ul>
                  <li>💧 Aim for 8-10 glasses of water per day</li>
                  <li>🥤 Include hydrating foods (fruits, vegetables)</li>
                  <li>🚫 Limit caffeine intake</li>
                  <li>⚡ Increase intake during exercise or hot weather</li>
                  <li>🚨 Watch for signs of dehydration (dark urine, dizziness)</li>
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* Meal Recommendations */}
        {!loading && mealPlan && (
          <section className="data-section meals-section">
            <h2>🍽️ {t('pregnancy.meal.recommendations', 'Meal Recommendations')}</h2>
            <MealPlanner userId={userId} />
          </section>
        )}

        {/* User Preferences */}
        {!loading && (
          <section className="data-section preferences-section">
            <UserPreferences userId={userId} onSave={loadAllData} />
          </section>
        )}
            </div>
          )}

          {/* Exercise Tab */}
          {activeTab === 'exercise' && (
            <div className="tab-panel">
        {/* Exercise */}
        {!loading && weekAdvice?.exercise && (
          <section className="data-section exercise-section">
            <h2>🏃 {t('pregnancy.exercise', 'Exercise')}</h2>
            <ExerciseGuide exerciseInfo={weekAdvice.exercise} />
          </section>
        )}
            </div>
          )}

          {/* Appointments Tab */}
          {activeTab === 'appointments' && (
            <div className="tab-panel">
        {/* Weekly Health Recommendations - Appointments & Tests */}
        {!loading && weekAdvice?.advice?.health && (
          <section className="data-section health-section">
            <h2>🏥 {t('pregnancy.weekly.health.recommendations', 'Weekly Health Recommendations')}</h2>
            <div className="nutrient-foods-grid">
              {weekAdvice.advice.health.appointments && weekAdvice.advice.health.appointments.length > 0 && (
                <div className="nutrient-category">
                  <h3>📅 {t('pregnancy.appointments', 'Appointments')}</h3>
                  <ul>
                    {weekAdvice.advice.health.appointments.map((a, i) => (
                      <li key={i}>{a}</li>
                    ))}
                  </ul>
                </div>
              )}
              {weekAdvice.advice.health.tests && weekAdvice.advice.health.tests.length > 0 && (
                <div className="nutrient-category">
                  <h3>🧪 {t('pregnancy.tests', 'Tests')}</h3>
                  <ul>
                    {weekAdvice.advice.health.tests.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ACOG Appointments Needed */}
        {!loading && weekAdvice?.acogAppointments && (
          <section className="data-section acog-appointments-section">
            <h2>📅 {t('pregnancy.acog.appointments', 'Appointments Needed (ACOG Guidelines)')}</h2>
            <div className="nutrient-foods-grid">
              {weekAdvice.acogAppointments.currentWeek && (
                <div className="nutrient-category" style={{position: 'relative', overflow: 'hidden', paddingLeft: 'calc(20px + 16px)'}}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: 'linear-gradient(180deg, #1976d2 0%, #2196f3 50%, #42a5f5 100%)',
                    borderRadius: '8px 0 0 8px',
                    opacity: 0.8
                  }}></div>
                  <h3>📋 {weekAdvice.acogAppointments.currentWeek.name || 'Current Week Appointment'}</h3>
                  <p style={{marginBottom: '10px', fontWeight: 'bold'}}>
                    Week {weekAdvice.acogAppointments.currentWeek.week}
                  </p>
                  {weekAdvice.acogAppointments.currentWeek.description && (
                    <p style={{marginBottom: '10px'}}>{weekAdvice.acogAppointments.currentWeek.description}</p>
                  )}
                  {weekAdvice.acogAppointments.currentWeek.includes && weekAdvice.acogAppointments.currentWeek.includes.length > 0 && (
                    <div>
                      <strong style={{display: 'block', marginBottom: '8px', fontSize: '0.95rem'}}>This visit includes:</strong>
                      <ul>
                        {weekAdvice.acogAppointments.currentWeek.includes.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {weekAdvice.acogAppointments.upcoming && weekAdvice.acogAppointments.upcoming.length > 0 && (
                <div className="nutrient-category">
                  <h3>🔜 {t('pregnancy.upcoming.appointments', 'Upcoming Appointments (Next 4 Weeks)')}</h3>
                  {weekAdvice.acogAppointments.upcoming.map((appt, i) => (
                    <div key={i} style={{marginBottom: '15px', paddingBottom: '15px', borderBottom: i < weekAdvice.acogAppointments.upcoming.length - 1 ? '1px solid #e0e0e0' : 'none'}}>
                      <strong style={{display: 'block', marginBottom: '5px'}}>Week {appt.week}: {appt.name}</strong>
                      {appt.description && (
                        <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '5px'}}>{appt.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {weekAdvice.acogAppointments.timeline && weekAdvice.acogAppointments.timeline.length > 0 && (
                <div className="nutrient-category">
                  <h3>📊 {t('pregnancy.appointment.timeline', 'Appointment Schedule')}</h3>
                  {weekAdvice.acogAppointments.timeline.map((period, i) => (
                    <div key={i} style={{marginBottom: '15px'}}>
                      <strong style={{display: 'block', marginBottom: '5px'}}>Weeks {period.weeks}</strong>
                      <p style={{fontSize: '0.9rem', color: '#666'}}>
                        {period.frequency}: {period.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
            </div>
          )}

          {/* Warnings Tab */}
          {activeTab === 'warnings' && (
            <div className="tab-panel">
        {/* Weekly Health Recommendations - Warnings */}
        {!loading && weekAdvice?.advice?.health?.warnings && weekAdvice.advice.health.warnings.length > 0 && (
          <section className="data-section health-warnings-section">
            <h2>⚠️ {t('pregnancy.health.warnings', 'Health Warnings')}</h2>
            <div className="nutrient-foods-grid">
              <div className="nutrient-category" style={{position: 'relative', overflow: 'hidden', paddingLeft: 'calc(20px + 16px)'}}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '4px',
                  height: '100%',
                  background: 'linear-gradient(180deg, #c62828 0%, #d32f2f 50%, #e53935 100%)',
                  borderRadius: '8px 0 0 8px',
                  opacity: 0.8
                }}></div>
                <h3>⚠️ {t('pregnancy.warnings', 'Warnings')}</h3>
                <ul>
                  {weekAdvice.advice.health.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* ACOG Warning Signs to Watch */}
        {!loading && weekAdvice?.acogWarningSigns && (
          <section className="data-section acog-warnings-section">
            <h2>🚨 {t('pregnancy.acog.warning.signs', 'Warning Signs to Watch (ACOG Red Flags)')}</h2>
            <div className="nutrient-foods-grid">
              {weekAdvice.acogWarningSigns.redFlags && weekAdvice.acogWarningSigns.redFlags.length > 0 && (
                <div className="nutrient-category nutrient-category-warning" style={{backgroundColor: '#fff5f5'}}>
                  <h3>⚠️ {t('pregnancy.red.flags', 'Red Flags - Call Doctor Immediately')}</h3>
                  {weekAdvice.acogWarningSigns.redFlags.map((category, i) => (
                    <div key={i} style={{marginBottom: '20px'}}>
                      <strong style={{display: 'block', marginBottom: '8px', color: '#c62828'}}>
                        {category.category}:
                      </strong>
                      {category.signs && category.signs.length > 0 && (
                        <ul style={{marginBottom: '8px'}}>
                          {category.signs.map((sign, j) => (
                            <li key={j} style={{marginBottom: '5px'}}>{sign}</li>
                          ))}
                        </ul>
                      )}
                      {category.action && (
                        <p style={{fontSize: '0.9rem', color: '#c62828', fontWeight: 'bold', marginTop: '8px'}}>
                          ⚡ {category.action}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {weekAdvice.acogWarningSigns.whenToCall && (
                <div className="nutrient-category">
                  <h3>📞 {t('pregnancy.when.to.call', 'When to Call Your Doctor')}</h3>
                  {weekAdvice.acogWarningSigns.whenToCall.immediately && weekAdvice.acogWarningSigns.whenToCall.immediately.length > 0 && (
                    <div style={{marginBottom: '15px'}}>
                      <strong style={{display: 'block', marginBottom: '8px', color: '#c62828'}}>
                        🚨 Call Immediately:
                      </strong>
                      <ul>
                        {weekAdvice.acogWarningSigns.whenToCall.immediately.map((item, i) => (
                          <li key={i} style={{color: '#c62828'}}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {weekAdvice.acogWarningSigns.whenToCall.within24Hours && weekAdvice.acogWarningSigns.whenToCall.within24Hours.length > 0 && (
                    <div style={{marginBottom: '15px'}}>
                      <strong style={{display: 'block', marginBottom: '8px', color: '#f57c00'}}>
                        ⏰ Call Within 24 Hours:
                      </strong>
                      <ul>
                        {weekAdvice.acogWarningSigns.whenToCall.within24Hours.map((item, i) => (
                          <li key={i} style={{color: '#f57c00'}}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {weekAdvice.acogWarningSigns.whenToCall.routine && weekAdvice.acogWarningSigns.whenToCall.routine.length > 0 && (
                    <div>
                      <strong style={{display: 'block', marginBottom: '8px', color: '#1976d2'}}>
                        📋 Routine Questions:
                      </strong>
                      <ul>
                        {weekAdvice.acogWarningSigns.whenToCall.routine.map((item, i) => (
                          <li key={i} style={{color: '#1976d2'}}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}
            </div>
          )}
        </div>
      </div>
      )}

      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={() => {}}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

export default CompletePregnancyView;

